import React, { useEffect, useState } from 'react';
import config from './config';
import './Gallery.css';

function Gallery({ onAddToCart }) {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');
  const [toastHide, setToastHide] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState('photo'); // 'photo' | 'video'
  const [activeVideo, setActiveVideo] = useState(null);
  const [fullscreenSrc, setFullscreenSrc] = useState(null);

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/api/artworks`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load artworks');
        return res.json();
      })
      .then(data => {
        setArtworks(data);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const handleAdd = (art) => {
    onAddToCart(art);
    setToast(`"${art.title}" added to cart!`);
    setToastHide(false);
    setTimeout(() => setToastHide(true), 1200);
    setTimeout(() => setToast(''), 1800);
  };

  const openModal = (artwork) => {
    setSelectedArtwork(artwork);
    setActiveImageIndex(0);
    setPreviewMode('photo');
    setActiveVideo(null);
    setFullscreenSrc(null);
  };

  const closeModal = () => {
    setSelectedArtwork(null);
    setActiveImageIndex(0);
    setPreviewMode('photo');
    setActiveVideo(null);
    setFullscreenSrc(null);
  };

  const handleModalClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      closeModal();
    }
  };

  const selectProcessPhoto = (idx) => {
    setActiveImageIndex(idx);
    setPreviewMode('photo');
    setActiveVideo(null);
  };

  const expandVideo = (video) => {
    setActiveVideo(video);
    setPreviewMode('video');
  };

  const processPhotos = selectedArtwork
    ? (selectedArtwork.media || []).filter(m => m.mediaType === 'IMAGE')
    : [];
  const processVideos = selectedArtwork
    ? (selectedArtwork.media || []).filter(m => m.mediaType === 'VIDEO')
    : [];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="gallery-container">
      <h2 className="gallery-title">Artworks Gallery</h2>
      {toast && <div className={`toast${toastHide ? ' hide' : ''}`}>{toast}</div>}
      <div className="gallery-grid">
        {artworks.map(art => (
          <div key={art.id} className="gallery-card">
            <img
              src={`${config.API_BASE_URL}${art.imageUrl}`}
              alt={art.title}
              className="gallery-image"
              style={{ cursor: 'pointer' }}
              onClick={() => openModal(art)}
            />
            <div
              className="gallery-title-text"
              style={{ cursor: 'pointer' }}
              onClick={() => openModal(art)}
            >
              {art.title}
            </div>
            <div className="artwork-details">
              <span className="artwork-price">₪{art.price}</span>
              <span className="artwork-size">{art.sizeX} x {art.sizeY} cm</span>
            </div>
            <button className="gallery-button" onClick={() => handleAdd(art)}>
              <i className="fa-solid fa-cart-plus"></i>
              Add to cart
            </button>
          </div>
        ))}
      </div>

      {selectedArtwork && (
        <div className="modal-overlay" onClick={handleModalClick}>
          <div className="modal-content artwork-detail-modal">
            <button className="modal-close" onClick={closeModal} aria-label="Close">
              <i className="fa-solid fa-times"></i>
            </button>

            <div className="artwork-detail-layout">
              <div className="artwork-detail-left">
                <button
                  type="button"
                  className="artwork-detail-main-btn"
                  onClick={() => setFullscreenSrc(`${config.API_BASE_URL}${selectedArtwork.imageUrl}`)}
                  title="View fullscreen"
                >
                  <img
                    src={`${config.API_BASE_URL}${selectedArtwork.imageUrl}`}
                    alt={selectedArtwork.title}
                    className="artwork-detail-main-image"
                  />
                </button>
              </div>

              <div className="artwork-detail-right">
                <h3>{selectedArtwork.title}</h3>
                <div className="artwork-detail-meta">
                  <span>₪{selectedArtwork.price}</span>
                  <span>{selectedArtwork.sizeX} x {selectedArtwork.sizeY} cm</span>
                </div>

                {selectedArtwork.description && (
                  <div className="artwork-detail-section">
                    <h4>About this artwork</h4>
                    <p className="artwork-detail-description">{selectedArtwork.description}</p>
                  </div>
                )}

                {(processPhotos.length > 0 || processVideos.length > 0) && (
                  <div className="artwork-detail-section">
                    <h4>How it was painted</h4>

                    <div className="artwork-process-grid">
                      {processPhotos.map((photo, idx) => (
                        <button
                          key={photo.id || `photo-${idx}`}
                          type="button"
                          className={`artwork-process-thumb${previewMode === 'photo' && activeImageIndex === idx ? ' active' : ''}`}
                          onClick={() => selectProcessPhoto(idx)}
                          title={photo.caption || `Process ${idx + 1}`}
                        >
                          <img
                            src={`${config.API_BASE_URL}${photo.url}`}
                            alt={photo.caption || `Process ${idx + 1}`}
                          />
                        </button>
                      ))}
                      {processVideos.map(video => (
                        <button
                          key={video.id || `video-${video.url}`}
                          type="button"
                          className={`artwork-process-thumb artwork-video-thumb-btn${previewMode === 'video' && activeVideo?.id === video.id ? ' active' : ''}`}
                          onClick={() => expandVideo(video)}
                          title={video.caption || 'Play video'}
                        >
                          <img
                            src={video.thumbnailUrl
                              ? `${config.API_BASE_URL}${video.thumbnailUrl}`
                              : `${config.API_BASE_URL}${selectedArtwork.imageUrl}`}
                            alt={video.caption || 'Video'}
                          />
                          <span className="artwork-video-play-badge">
                            <i className="fa-solid fa-play"></i>
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="artwork-media-stage">
                      {previewMode === 'video' && activeVideo ? (
                        <video
                          key={activeVideo.id}
                          className="artwork-process-preview artwork-process-preview-video"
                          controls
                          autoPlay
                          muted
                          playsInline
                          poster={activeVideo.thumbnailUrl ? `${config.API_BASE_URL}${activeVideo.thumbnailUrl}` : undefined}
                          src={`${config.API_BASE_URL}${activeVideo.url}`}
                        >
                          <source src={`${config.API_BASE_URL}${activeVideo.url}`} type="video/mp4" />
                        </video>
                      ) : processPhotos[activeImageIndex] ? (
                        <img
                          className="artwork-process-preview"
                          src={`${config.API_BASE_URL}${processPhotos[activeImageIndex].url}`}
                          alt={processPhotos[activeImageIndex].caption || 'Process photo'}
                        />
                      ) : null}
                    </div>
                  </div>
                )}

                <button className="btn-animate modal-add-btn" onClick={() => handleAdd(selectedArtwork)}>
                  <i className="fa-solid fa-cart-plus" style={{ marginRight: 8 }}></i>
                  Add to cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {fullscreenSrc && (
        <div
          className="artwork-fullscreen-overlay"
          onClick={() => setFullscreenSrc(null)}
          role="presentation"
        >
          <button
            type="button"
            className="artwork-fullscreen-close"
            onClick={() => setFullscreenSrc(null)}
            aria-label="Close fullscreen"
          >
            <i className="fa-solid fa-times"></i>
          </button>
          <img
            src={fullscreenSrc}
            alt="Fullscreen artwork"
            className="artwork-fullscreen-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Gallery;
